/* global Helpers */
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { getCurrentAccountSelector, getCurrentUserSelector } from 'selectors'
import { formatHoursToFirstContact } from 'lib/helpers'
import userNameForListing from 'modules/userNameForListing'

import { H6, P } from '@PipelineDeals/shared-react-components'

import { Section } from 'components/BusinessCard/components/StyledComponents'

import { getDealStatuses, getLeadSources } from 'selectors/Account'
import { useBusinessCard } from 'components/BusinessCard/context'

import CompanyName from 'components/BusinessCard/components/CompanyName'
import CreatedUpdated from 'components/BusinessCard/components/CreatedUpdated'
import DealOwner from 'components/BusinessCard/components/DealOwner'
import DealStagePipeline from 'components/BusinessCard/components/DealStagePipeline'
import Description from 'components/BusinessCard/components/Description'
import PrimaryContact from 'components/BusinessCard/components/PrimaryContact'
import SimpleSection from 'components/BusinessCard/components/SimpleSection'
import Tags from 'components/BusinessCard/components/Tags'
import Addresses from 'components/BusinessCard/components/Addresses'

import styled from 'styled-components'

const Divider = styled.hr`
  border: none;
  background-color: ${({ theme }) => theme.colors.gray[200]};
  margin: 4px 0;
  height: 1px;
`

const DealAmount = styled.span`
  color: ${({ theme }) => theme.colors.green[500]};
  font-weight: normal;
`

const Deal = () => {
  const { t } = useTranslation()
  const {
    cardMeta: {
      showAssociationsCount,
      showMainDivider
    },
    resource: deal
  } = useBusinessCard()

  const dealStatuses = useSelector(getDealStatuses)
  const dealSources = useSelector(getLeadSources)
  const selectedCurrencies = useSelector(getCurrentAccountSelector).selected_currencies
  const dealStages = useSelector(getCurrentAccountSelector).deal_stages
  const currentUser = useSelector(getCurrentUserSelector)
  const visibleTeams = currentUser.visible_teams

  const associationsCount = useMemo(() => {
    if (!deal.person_ids) { return null }
    const count = deal.person_ids.length
    const peopleCount = `(${count}) ${count === 1 ? t('common.resources.person') : t('businessCard.labels.people')}`
    return (
      <Section>
        <H6>{t('businessCard.labels.peopleCount')}</H6>
        <P>{peopleCount}</P>
      </Section>
    )
  }, [deal])

  const dealStatus = useMemo(() => {
    if (!deal.status) { return null }

    const status = dealStatuses.find(ds => ds.id === deal.status)
    if (!status) { return null }

    return <span><i className="fas fa-circle" style={{ color: `#${status.hex_color}` }} /> {status.name}</span>
  }, [deal, dealStatuses])

  const dealSource = useMemo(() => {
    if (deal.source) { return deal.source.name }
    if (!deal.source_id) { return null }

    const source = dealSources.find(ds => ds.id === deal.source_id)
    return source?.name
  }, [deal, dealSources])

  const dealAge = useMemo(() => {
    if (deal.created_at) {
      const days = Helpers.daysSince(deal.created_at)
      return `${days} ${days === 1 ? t('businessCard.units.day') : t('businessCard.units.days')}`
    } else {
      return ''
    }
  }, [deal])

  const bonus = useMemo(() => {
    const { bonus, currency_id: currencyId } = deal

    if (bonus) {
      const currency = selectedCurrencies.find(c => c.id === currencyId) || currentUser.currency
      return Helpers.formatCurrency(
        bonus,
        currency ? currency.symbol : undefined
      )
    } else {
      return ''
    }
  }, [deal])

  const commission = useMemo(() => {
    const { commission, currency_id: currencyId } = deal

    if (commission) {
      const currency = selectedCurrencies.find(c => c.id === currencyId) || currentUser.currency
      return Helpers.formatCurrency(
        commission,
        currency ? currency.symbol : undefined
      )
    } else {
      return ''
    }
  }, [deal])

  const currency = useMemo(() => {
    const { currency_id: currencyId } = deal

    const currency = selectedCurrencies.find(c => c.id === currencyId)
    if (currency) {
      return `${currency.code} - ${currency.symbol}`
    } else {
      return ''
    }
  }, [deal])

  const latestDealStageChange = useMemo(() => {
    const { latest_deal_stage_change: dealStageChange, deal_stage_id: dealStageId } = deal

    const currentStage = dealStages.find(ds => ds.id === dealStageId)

    if (!dealStageChange || !currentStage || ![0, 100].includes(currentStage.percent) || !dealStageChange.old_deal_stage_name) {
      return ''
    }
    return dealStageChange.old_deal_stage_name
  }, [deal])

  const timeToClose = useMemo(() => {
    const { deal_time_to_close: dealTimeToClose } = deal
    if (dealTimeToClose) {
      return `${dealTimeToClose} ${dealTimeToClose === 1 ? t('businessCard.units.day') : t('businessCard.units.days')}`
    } else {
      return ''
    }
  }, [deal])

  const weightedForecast = useMemo(() => {
    const { deal_weighted_forecast: dealWeightedForecast, currency_id: currencyId } = deal
    const currency = selectedCurrencies.find(c => c.id === currencyId) || currentUser.currency
    return Helpers.formatCurrencyCustomField(dealWeightedForecast, currency ? currency.symbol : undefined)
  }, [deal])

  const ownerTeam = useMemo(() => {
    const { user } = deal
    if (!user) return ''
    const team = visibleTeams.find(t => t.id === user.team_id)
    return team ? team.name : ''
  }, [deal])

  const sharedWith = useMemo(() => {
    const { cached_shared_user_ids: cachedSharedUserIds } = deal
    const sharedUsers = (cachedSharedUserIds || []).map(id => userNameForListing(id)).join(', ')
    if (!sharedUsers.length) return ''
    return <div dangerouslySetInnerHTML={{ __html: sharedUsers }} />
  }, [deal])

  return (
    <>
      <DealOwner />

      <DealStagePipeline />
      <SimpleSection
        field="value"
        value={deal.value ? <DealAmount>{deal.currency_symbol || deal.currency?.symbol}{deal.value}</DealAmount> : null}
        label={t('businessCard.labels.amount')}
      />

      <SimpleSection
        field="deal_value"
        value={deal.deal_value ? <DealAmount>{deal.currency_symbol || deal.currency?.symbol}{deal.deal_value}</DealAmount> : null}
        label={t('businessCard.labels.amount')}
      />

      <CompanyName />

      {showMainDivider && <Divider />}

      <SimpleSection field="status" value={dealStatus} label={t('businessCard.labels.status')} />
      <SimpleSection field="source_id" value={dealSource} label={t('businessCard.labels.source')} />
      <SimpleSection field="product_line_ids" value={deal.product_lines?.map(pl => pl.name).join(', ')} label={t('businessCard.labels.performanceLane')} />
      <SimpleSection field="revenue_type_id" value={deal.revenue_type?.name} label={t('businessCard.labels.revenueType')} />
      <SimpleSection field="probability" value={deal.probability ? `${deal.probability}%` : null} label={t('businessCard.labels.probability')} />

      <PrimaryContact />
      <Description />
      <Tags />

      <SimpleSection field="team_id" value={ownerTeam} label={t('businessCard.labels.ownersTeam')} />
      <SimpleSection field="shared_user_ids" value={sharedWith} label={t('businessCard.labels.sharedWith')} />

      <SimpleSection field="next_entry_name" value={deal.next_entry_name} label={t('businessCard.labels.nextTask')} />
      <SimpleSection field="next_entry_due" value={deal.next_entry_all_day ? Helpers.formatDate(deal.next_entry_due) : Helpers.formatDateTime(deal.next_entry_due)} label={t('businessCard.labels.nextTaskDue')} />

      <SimpleSection field="deal_loss_reason_id" value={deal.deal_loss_reason?.name} label={t('businessCard.labels.lossReason')} />
      <Description field="deal_loss_reason_notes" value={deal.deal_loss_reason_notes} label={t('businessCard.labels.lossReasonNotes')} />
      <SimpleSection field="deal_won_reason_id" value={deal.deal_won_reason?.name} label={t('businessCard.labels.wonReason')} />
      <Description field="deal_won_reason_notes" value={deal.deal_won_reason_notes} label={t('businessCard.labels.wonReasonNotes')} />

      <SimpleSection field="deal_weighted_forecast" value={weightedForecast} label={t('businessCard.labels.weightedForecast')} />
      <SimpleSection field="bonus" value={bonus} label={t('businessCard.labels.bonus')} />
      <SimpleSection field="commission" value={commission} label={t('businessCard.labels.commission')} />
      <SimpleSection field="commission_rate" value={deal.commission_rate ? `${deal.commission_rate}%` : ''} label={t('businessCard.labels.commissionRate')} />
      <SimpleSection field="currency_id" value={currency} label={t('businessCard.labels.currency')} />

      <SimpleSection field="deal_days_active" value={deal.deal_days_active} label={t('businessCard.labels.daysActive')} />
      <SimpleSection field="date_of_first_activity" value={Helpers.formatDateTime(deal.date_of_first_activity)} label={t('businessCard.labels.firstActivity')} />
      <SimpleSection field="hours_to_first_activity" value={formatHoursToFirstContact(deal.hours_to_first_activity)} label={t('businessCard.labels.timeToFirstActivity')} />
      <SimpleSection field="days_in_stage" value={deal.days_in_stage} label={t('businessCard.labels.daysInStage')} />
      <SimpleSection field="days_since_last_activity" value={deal.days_since_last_activity} label={t('businessCard.labels.daysSinceLastActivity')} />
      <SimpleSection field="latest_deal_stage_change" value={latestDealStageChange} label={t('businessCard.labels.stageBeforeClosed')} />
      <SimpleSection field="deal_time_to_close" value={timeToClose} label={t('businessCard.labels.timeToClose')} />

      <SimpleSection field="import.import_name" value={deal.import?.import_name} label={t('businessCard.labels.import')} />
      <SimpleSection field="imported_at" value={Helpers.formatDate(deal.imported_at)} label={t('businessCard.labels.imported')} />

      <SimpleSection field="is_archived" value={deal.is_archived ? t('businessCard.values.yes') : t('businessCard.values.no')} label={t('businessCard.labels.archived')} />
      <Addresses addressTypes={['address']} />

      <SimpleSection field="created_by_user_id" value={<div dangerouslySetInnerHTML={{ __html: userNameForListing(deal.created_by_user_id) }} />} label={t('businessCard.labels.createdBy')} />
      <SimpleSection field="updated_by_user_id" value={<div dangerouslySetInnerHTML={{ __html: userNameForListing(deal.updated_by_user_id) }} />} label={t('businessCard.labels.updatedBy')} />

      <SimpleSection field="created_at" value={dealAge} label={t('businessCard.labels.age')} />
      <SimpleSection field="expected_close_date" value={Helpers.formatDate(deal.expected_close_date)} label={t('businessCard.labels.expectedClose')} />
      <SimpleSection field="closed_time" value={Helpers.formatDate(deal.closed_time)} label={t('businessCard.labels.actualClose')} />
      <CreatedUpdated />

      {showAssociationsCount && associationsCount}
    </>
  )
}

export default Deal
